import React, { useCallback, useEffect, useRef, useState } from "react";

import { useLocalization } from "@fluent/react";
import { Button, ProgressBar, Row } from "@narmi/design_system";
import { Dialog, useNotificationContext } from "cerulean";

import { formatReceiptAmount, formatReceiptDate } from "../../format";
import { useReceiptUploadPolling } from "../../hooks/useReceiptsList";

import Dropzone from "./Dropzone";
import { pickErrorMapping } from "./errorMapping";
import useReceiptUpload from "./useReceiptUpload";
import useReceiptUploadConfig from "./useReceiptUploadConfig";
import styles from "./UploadReceiptDialog.module.scss";

import type { ReceiptListRow } from "../../hooks/useReceiptsList";

interface UploadReceiptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: (uuid: string) => void;
  onReceiptRemoved?: (uuid: string) => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const extractBackendCode = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const { response } = error as { response?: { data?: unknown } };
  if (
    !response ||
    typeof response.data !== "object" ||
    response.data === null
  ) {
    return undefined;
  }
  const { id } = response.data as { id?: unknown };
  return typeof id === "string" ? id : undefined;
};

const extractBackendMessage = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const { response } = error as { response?: { data?: unknown } };
  if (
    !response ||
    typeof response.data !== "object" ||
    response.data === null
  ) {
    return undefined;
  }
  const { message } = response.data as { message?: unknown };
  return typeof message === "string" ? message : undefined;
};

interface DetailRowProps {
  label: string;
  value: string;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <Row justifyContent="space-between">
    <Row.Item>
      <span className="fontColor--secondary">{label}</span>
    </Row.Item>
    <Row.Item shrink>
      <span className="fontColor--primary">{value}</span>
    </Row.Item>
  </Row>
);

interface InProgressRowProps {
  fileName: string;
  label: string;
  percentComplete: number;
  testId: string;
}

// Single visual for both the upload-in-flight phase and the post-upload
// polling phase. Per Figma 113:5937 the two share the same file-row + progress
// bar treatment; we hold the bar at 100% during the reading phase so the user
// sees a continuous "still working" affordance.
const InProgressRow = ({
  fileName,
  label,
  percentComplete,
  testId,
}: InProgressRowProps) => (
  <div data-testid={testId}>
    <div className={styles["file-row"]}>
      <span className="narmi-icon-file-text" aria-hidden="true" />
      <div className={styles["file-row-name"]}>
        <div>{fileName}</div>
        <div className="fontColor--secondary fontSize--s">{label}</div>
      </div>
    </div>
    <div className={styles["progress-bar"]}>
      <ProgressBar percentComplete={percentComplete} size="s" />
    </div>
  </div>
);

interface CompletedFileRowProps {
  fileName: string;
  fileSize: number;
  onRemove: () => void;
  removeLabel: string;
  disabled?: boolean;
}

// File row used in both the extracted (success) and failed phases — green check,
// filename, size, and an X affordance to drop back to the dropzone for another
// upload. The footer's "Upload another" button does the same; the X is the
// inline equivalent per Figma 113:6316.
const CompletedFileRow = ({
  fileName,
  fileSize,
  onRemove,
  removeLabel,
  disabled = false,
}: CompletedFileRowProps) => (
  <div className={styles["file-row"]}>
    <span
      className="narmi-icon-check-circle fontColor--success"
      aria-hidden="true"
    />
    <div className={styles["file-row-name"]}>{fileName}</div>
    <div className="fontColor--secondary fontSize--s">
      {formatFileSize(fileSize)}
    </div>
    <button
      type="button"
      className={styles["file-row-remove"]}
      onClick={onRemove}
      disabled={disabled}
      aria-label={removeLabel}
      data-testid="upload-receipt-remove"
    >
      <span className="narmi-icon-x" aria-hidden="true" />
    </button>
  </div>
);

interface ExtractedPanelProps {
  row: ReceiptListRow;
}

const ExtractedPanel = ({ row }: ExtractedPanelProps) => {
  const { l10n } = useLocalization();
  return (
    <div
      data-testid="upload-receipt-extracted"
      className={`${styles["receipt-details"]} margin--top--l border--all rounded--all--m padding--all--m`}
    >
      <div
        className={`${styles["receipt-details-heading"]} fontColor--secondary fontWeight--semibold`}
      >
        {l10n.getString("receipts-upload-details-heading")}
      </div>
      <div className="margin--top--s">
        <DetailRow
          label={l10n.getString("receipts-upload-details-merchant-label")}
          value={row.merchant ?? "—"}
        />
      </div>
      <div className="margin--top--xs">
        <DetailRow
          label={l10n.getString("receipts-upload-details-date-label")}
          value={formatReceiptDate(row.transaction_date) || "—"}
        />
      </div>
      <div className="margin--top--xs">
        <DetailRow
          label={l10n.getString("receipts-upload-details-amount-label")}
          value={formatReceiptAmount(row.amount) || "—"}
        />
      </div>
    </div>
  );
};

// Inline duplicate of the EXTRACTION_FAILED placeholder treatment from
// UnmatchedTable. Extraction is deferred to a separate refactor card once a
// third consumer appears.
const FailedPanel = () => {
  const { l10n } = useLocalization();
  return (
    <div
      data-testid="upload-receipt-failed"
      className={`${styles["receipt-details"]} margin--top--l border--all rounded--all--m padding--all--m`}
    >
      <div
        className={`${styles["receipt-details-heading"]} fontColor--secondary fontWeight--semibold`}
      >
        {l10n.getString("receipts-upload-details-heading")}
      </div>
      <div className="margin--top--s">
        <DetailRow
          label={l10n.getString("receipts-upload-details-merchant-label")}
          value={l10n.getString("receipts-unmatched-no-merchant")}
        />
      </div>
      <div className="margin--top--xs">
        <DetailRow
          label={l10n.getString("receipts-upload-details-date-label")}
          value={l10n.getString("receipts-unmatched-no-date")}
        />
      </div>
      <div className="margin--top--xs">
        <DetailRow
          label={l10n.getString("receipts-upload-details-amount-label")}
          value={l10n.getString("receipts-unmatched-no-amount")}
        />
      </div>
      <div className="margin--top--s fontColor--secondary fontSize--s">
        {l10n.getString("receipts-reason-extraction-failed")}
      </div>
    </div>
  );
};

const TimeoutPanel = () => {
  const { l10n } = useLocalization();
  return (
    <div
      data-testid="upload-receipt-timeout"
      className="margin--top--l fontColor--secondary"
    >
      {l10n.getString("receipts-upload-timeout-body")}
    </div>
  );
};

const UploadReceiptDialog = ({
  isOpen,
  onClose,
  onUploadSuccess,
  onReceiptRemoved,
}: UploadReceiptDialogProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { status, progress, response, upload, remove, reset, abort } =
    useReceiptUpload();
  const { status: configStatus, config } = useReceiptUploadConfig(isOpen);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const lastNotifiedUuidRef = useRef<string | null>(null);
  const configErrorHandledRef = useRef(false);

  // Hoisted to the top of the component body per rules-of-hooks. The hook's
  // own `enabled` gate keeps it dormant until the upload succeeds; passing
  // undefined here resets its internal timers when the modal closes or resets.
  const polling = useReceiptUploadPolling(
    status === "success" ? response?.uuid : undefined
  );

  useEffect(() => {
    if (status !== "success" || !response?.uuid) return;
    if (lastNotifiedUuidRef.current === response.uuid) return;
    lastNotifiedUuidRef.current = response.uuid;
    onUploadSuccess?.(response.uuid);
  }, [status, response, onUploadSuccess]);

  const fireErrorToast = useCallback(
    (backendCode: string | undefined) => {
      const maxSizeBytes = config?.max_size_bytes ?? 0;
      const { ftlId, vars } = pickErrorMapping(backendCode, maxSizeBytes);
      sendNotification({
        type: "negative",
        text: l10n.getString(ftlId, vars),
      });
    },
    [config, l10n, sendNotification]
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!config) {
        return;
      }
      if (!config.allowed_mime_types.includes(file.type)) {
        fireErrorToast("unsupported_mime_type");
        return;
      }
      if (file.size > config.max_size_bytes) {
        fireErrorToast("file_too_large");
        return;
      }
      setSelectedFile(file);
      try {
        await upload(file);
      } catch (error) {
        // The hook swallows cancellations; anything that reaches here is a
        // real backend rejection or network failure.
        fireErrorToast(extractBackendCode(error));
      }
    },
    [config, fireErrorToast, upload]
  );

  const handleClose = useCallback(() => {
    if (status === "uploading") {
      abort();
    }
    onClose();
  }, [abort, onClose, status]);

  const handleUploadAnother = useCallback(() => {
    reset();
    setSelectedFile(null);
  }, [reset]);

  const handleRemove = useCallback(async () => {
    const uuid = response?.uuid;
    // No persisted receipt yet (defensive) — just drop back to the dropzone.
    if (!uuid) {
      handleUploadAnother();
      return;
    }
    setIsRemoving(true);
    try {
      await remove(uuid);
      onReceiptRemoved?.(uuid);
      reset();
      setSelectedFile(null);
    } catch (error) {
      // Prefer the backend's localized message (e.g. the 409 "unlink the match
      // first" copy); fall back to the generic mapping for network failures.
      const backendMessage = extractBackendMessage(error);
      if (backendMessage) {
        sendNotification({ type: "negative", text: backendMessage });
      } else {
        fireErrorToast(extractBackendCode(error));
      }
    } finally {
      setIsRemoving(false);
    }
  }, [
    response,
    remove,
    onReceiptRemoved,
    reset,
    handleUploadAnother,
    fireErrorToast,
    sendNotification,
  ]);

  useEffect(() => {
    if (!isOpen) {
      if (status === "uploading") {
        abort();
      }
      reset();
      setSelectedFile(null);
      configErrorHandledRef.current = false;
    }
  }, [isOpen, status, abort, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (configStatus !== "error") return;
    if (configErrorHandledRef.current) return;
    configErrorHandledRef.current = true;
    sendNotification({
      type: "negative",
      text: l10n.getString("receipts-upload-config-fetch-error"),
    });
    onClose();
  }, [isOpen, configStatus, l10n, onClose, sendNotification]);

  const renderBody = () => {
    if (status === "success" && selectedFile) {
      return (
        <div data-testid="upload-receipt-success">
          {polling.phase === "reading" ? (
            // Upload has finished but extraction may still be in flight. While
            // polling reports `reading`, reuse the upload's file-row +
            // progress-bar visual so the user sees one continuous "we're
            // working on it" affordance. The progress bar holds at 100% —
            // upload is done, the remaining work is server-side extraction.
            <InProgressRow
              fileName={selectedFile.name}
              label={l10n.getString("receipts-upload-processing-label")}
              percentComplete={100}
              testId="upload-receipt-reading"
            />
          ) : (
            <>
              <CompletedFileRow
                fileName={selectedFile.name}
                fileSize={selectedFile.size}
                onRemove={handleRemove}
                removeLabel={l10n.getString("receipts-upload-action-remove")}
                disabled={isRemoving}
              />
              {polling.phase === "extracted" && polling.row && (
                <ExtractedPanel row={polling.row} />
              )}
              {polling.phase === "failed" && <FailedPanel />}
              {polling.phase === "timeout" && <TimeoutPanel />}
            </>
          )}
        </div>
      );
    }

    if (status === "uploading" && selectedFile) {
      return (
        <InProgressRow
          fileName={selectedFile.name}
          label={l10n.getString("receipts-upload-uploading-label")}
          percentComplete={progress}
          testId="upload-receipt-progress"
        />
      );
    }

    if (configStatus !== "ready" || !config) {
      return (
        <Dropzone
          onFileSelected={handleFileSelected}
          disabled
          acceptMimeTypes={config?.allowed_mime_types}
        />
      );
    }

    return (
      <Dropzone
        onFileSelected={handleFileSelected}
        acceptMimeTypes={config.allowed_mime_types}
      />
    );
  };

  const renderFooter = () => {
    if (status !== "success") return undefined;
    // Per Figma 113:5937, the footer actions appear only once extraction has
    // resolved — while polling we want a clean "Processing…" affordance.
    if (polling.phase === "reading") return undefined;
    return (
      <Row justifyContent="end" alignItems="center" gapSize="s">
        <Row.Item shrink>
          <Button
            kind="plain"
            label={l10n.getString("receipts-upload-action-done")}
            onClick={onClose}
            testId="upload-receipt-done"
          />
        </Row.Item>
        <Row.Item shrink>
          <Button
            kind="primary"
            label={l10n.getString("receipts-upload-action-upload-another")}
            onClick={handleUploadAnother}
            testId="upload-receipt-upload-another"
          />
        </Row.Item>
      </Row>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("receipts-upload-dialog-title")}
      headerStyle="bordered"
      onUserDismiss={handleClose}
      footer={renderFooter()}
      testId="upload-receipt-dialog"
    >
      {renderBody()}
    </Dialog>
  );
};

export default UploadReceiptDialog;
